import {Formik, Form, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import FormikControl, {ErrorFeedback} from '../../../components/formikControl/FormikControl'
import {fetchDriverList} from '../driver/api'
import {fetchShiftList} from '../shift/api'
import {useQueries} from 'react-query'
import {RunnerBall} from '../../../components/FormLoader'
import {fetchBusList} from '../bus/api'
import {addMinutes, isAfter, parse} from 'date-fns'
import {Button, Col, Row} from 'react-bootstrap'
import toast from 'react-hot-toast'
import {useFileUpload} from '../../hooks/useFileUpload'
import {handleApiError} from '../../../helpers/errorHandler'
import {studentEnrollment} from './api'
import {prepareStuentEnrollmentPayload} from './service'
import FileDrop from '../../../components/excelUpload/FileDrop'
import UploadFile from '../../../components/excelUpload/UploadFile'

function TripForm({onAfterSubmit}) {
  const calls = [
    {queryKey: 'shift', queryFn: fetchShiftList, refetchOnWindowFocus: false},
    {queryKey: 'driver', queryFn: fetchDriverList, refetchOnWindowFocus: false},
    {queryKey: 'bus', queryFn: fetchBusList, refetchOnWindowFocus: false},
  ]
  const results = useQueries(calls)
  const {uploadFile, isUploading, uploadProgress} = useFileUpload()

  if (results[0].isFetching || results[1].isFetching || results[2].isFetching) {
    return <RunnerBall />
  }
  const shiftList = results[0]?.data?.data ?? []
  const busList = results[2]?.data?.data ?? []

  const submitHandler = async (values, {setSubmitting: setLoading}) => {
    setLoading(true)
    let toastId
    try {
      const fileResponse = await uploadFile(values.file)

      toastId = toast.loading('Processing data')

      const payload = {...values, file: fileResponse.data}
      const studentEnrollmentPayload = prepareStuentEnrollmentPayload(payload)
      await studentEnrollment(studentEnrollmentPayload)
      toast.success('Success', {id: toastId})
      onAfterSubmit()
    } catch (error) {
      handleApiError(error, toastId)
    } finally {
      setLoading(false)
    }
  }
  const formValues = {
    shift_id: '',
    trip_start_time: '',
    trip_end_time: '',
    trip_type: '',
    bus_name: '',
    bus_number: '',
    file: '',
  }
  const tripTypes = [{tripType: 'pickup'}, {tripType: 'drop'}]

  return (
    <div className="px-8 py-6">
      <Formik
        initialValues={formValues}
        validationSchema={Yup.object({
          bus_name: Yup.string().required('Bus is required'),
          bus_number: Yup.string().required('Bus is required'),
          shift_id: Yup.string().required('Shift is required'),
          trip_type: Yup.string().required('Trip Type is required'),
          trip_start_time: Yup.string().required('Start Time is required'),
          trip_end_time: Yup.string()
            .test(
              'is-at-least-30-minutes-after',
              'End Time must be at least 30 minutes after Start Time',
              function (value) {
                const {trip_start_time} = this.parent

                // If trip_start_time or end_time is not provided, skip validation
                if (!trip_start_time || !value) return true

                // Parse trip_start_time and end_time into Date objects
                const parsedStartTime = parse(trip_start_time, 'HH:mm', new Date())
                const parsedEndTime = parse(value, 'HH:mm', new Date())

                // Add 30 minutes to the start time
                const startTimePlusMins = addMinutes(parsedStartTime, 29)

                // Check if end_time is at least 30 minutes after trip_start_time
                return isAfter(parsedEndTime, startTimePlusMins)
              }
            )
            .required('End Time is required'),
          file: Yup.mixed().required('File is required'),
        })}
        onSubmit={submitHandler}
      >
        {({isSubmitting, resetForm, setFieldValue, values, setFieldTouched}) => (
          <Form>
            <Row>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={tripTypes}
                  placeholder="Select Trip Type"
                  name="trip_type"
                  label="Trip Type"
                  value={tripTypes.find((trip) => trip.tripType === values.trip_type) || ''}
                  optionLabel={'tripType'}
                  optionValue={'tripType'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control={'select-advanced'}
                  options={shiftList}
                  placeholder="Select Shift"
                  label="Shift"
                  name="shift_id"
                  value={shiftList.find((shift) => shift.shift_id === values.shift_id) || ''}
                  optionLabel={'shift_name'}
                  optionValue={'shift_id'}
                  required
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="input"
                  required
                  type="time"
                  placeholder="Start Time"
                  label="Start Time"
                  name="trip_start_time"
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="input"
                  required
                  type="time"
                  placeholder="End Time"
                  label="End Time"
                  name="trip_end_time"
                />
              </Col>
              <Col sm="6">
                <FormikControl
                  control="select-advanced"
                  required
                  placeholder="Select Bus"
                  label="Bus"
                  name="bus_name"
                  optionValue="bus_id"
                  value={
                    busList.find(
                      (bus) => bus.bus_name + bus.bus_number === values.bus_name + values.bus_number
                    ) || ''
                  }
                  onChange={(selected, {action}) => {
                    setFieldValue('bus_name', selected?.bus_name || '')
                    setFieldValue('bus_number', selected?.bus_number || '')
                  }}
                  options={busList}
                  getOptionLabel={(option) => option.bus_name + '-' + option.bus_number}
                />
              </Col>
              <Col sm="12">
                {values.file ? (
                  <UploadFile
                    file={values.file}
                    removeFile={() => setFieldValue('file', '')}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                  />
                ) : (
                  <>
                    <FileDrop
                      onDropRejected={() =>
                        toast.error(
                          'Provided file is not supported. Please upload a file with .xlsx extension.'
                        )
                      }
                      onDropAccepted={(files) => {
                        setFieldValue('file', files[0])
                        setFieldTouched('file', true)
                      }}
                      templateLink="/excel-template/trip-upload-template-model.xlsx"
                      templateFileName="trip-upload-template-model.xlsx"
                      supportText="The order and placement of the cells in the
                      3rd row must remain consistent and should match exactly as shown in the sample template."
                    />
                    <ErrorMessage name="file" component={ErrorFeedback} />
                  </>
                )}
              </Col>
            </Row>

            <div className="text-center pt-8">
              <button
                type="reset"
                onClick={resetForm}
                className="btn btn-light px-8 me-4 border border-gray-200"
                disabled={isSubmitting}
              >
                Reset
              </button>
              <Button
                variant="light-success"
                className="pe-3 bg-light-success"
                disabled={isSubmitting}
                type="submit"
              >
                <span className="">
                  Upload <i className="la la-upload fs-4"></i>
                </span>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default TripForm
